import '../styles/LoginVerificationBand.css';
import React, { useState } from 'react';
import { useProjectCollection } from '../lib/projectData';

const UNLOCKED_READOUTS = [
  { label: 'Dashboard', value: 'Balance & ledger' },
  { label: 'Transfer', value: 'Move funds' },
  { label: 'Transfer History', value: 'Past transfers' },
  { label: 'Operations', value: 'Staff servicing' },
];

const IDENTIFIER_TYPES = ['client', 'operations_staff'];

function LoginVerificationBand({ onSubmit }) {
  const { data, error: accountsError, loading: accountsLoading } =
    useProjectCollection('client_accounts');

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [submissionStatus, setSubmissionStatus] = useState('idle');
  const [verificationError, setVerificationError] = useState('');

  const accounts = Array.isArray(data) ? data : [];
  const submitting = submissionStatus === 'submitting';

  const validate = () => {
    const next = {};
    if (!identifier.trim()) next.identifier = 'Enter your account identifier.';
    if (!password) next.password = 'Enter your password.';
    return next;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const nextErrors = validate();
    setFieldErrors(nextErrors);
    setVerificationError('');
    if (Object.keys(nextErrors).length > 0) return;

    setSubmissionStatus('submitting');

    // Awaited external verification boundary — the page may leave onSubmit unset.
    let result;
    if (typeof onSubmit === 'function') {
      result = await onSubmit({ identifier: identifier.trim(), password });
    } else {
      result = { ok: false, reason: 'unavailable', accountType: '' };
    }

    if (result && result.ok) {
      const type = IDENTIFIER_TYPES.indexOf(result.accountType) >= 0
        ? result.accountType
        : 'client';
      const destination = type === 'operations_staff' ? '/operations' : '/dashboard';
      setSubmissionStatus('verified');
      if (typeof window !== 'undefined' && window.location) {
        window.location.assign(destination);
      }
      return;
    }

    setSubmissionStatus('error');
    if (result && result.reason === 'unavailable') {
      setVerificationError(
        'Verification is not available in this preview. Your entries are preserved.'
      );
    } else {
      setVerificationError(
        'Those credentials could not be verified. Check your entries and try again.'
      );
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    if (field === 'identifier') setIdentifier(value);
    if (field === 'password') setPassword(value);
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
    if (verificationError) setVerificationError('');
  };

  return (
    <section className="lvb-root" aria-labelledby="lvb-title">
      <div className="lvb-inner">
        {/* ================= LEFT 7 — credential panel ================= */}
        <div className="lvb-panel">
          <div className="lvb-panel-head">
            <span className="lvb-micro">Returning Verification</span>
            <h1 className="lvb-title" id="lvb-title">
              Sign in to your account
            </h1>
            <div className="lvb-rule-h" aria-hidden="true" />
            <p className="lvb-lede">
              Enter the credentials issued to your account. Retail clients reach their
              balance and ledger; bank operations staff reach account servicing and
              transfer oversight. Verification reads the instrument, it does not alter it.
            </p>
          </div>

          <form className="lvb-form" onSubmit={handleSubmit} noValidate>
            {/* ---- CredentialFields : identifier ---- */}
            <div className="lvb-row">
              <div className="lvb-row-head">
                <label className="lvb-label" htmlFor="lvb-identifier">
                  Identifier
                </label>
                <span className="lvb-row-index">01</span>
              </div>
              <input
                id="lvb-identifier"
                className={
                  fieldErrors.identifier ? 'lvb-control is-invalid' : 'lvb-control'
                }
                type="text"
                name="identifier"
                autoComplete="username"
                placeholder="Account identifier or staff email"
                value={identifier}
                onChange={handleChange('identifier')}
                aria-invalid={Boolean(fieldErrors.identifier)}
                aria-describedby={
                  fieldErrors.identifier ? 'lvb-identifier-error' : undefined
                }
                disabled={submitting}
              />
              {fieldErrors.identifier && (
                <p className="lvb-hint" id="lvb-identifier-error">
                  {fieldErrors.identifier}
                </p>
              )}
            </div>

            {/* ---- CredentialFields : password ---- */}
            <div className="lvb-row">
              <div className="lvb-row-head">
                <label className="lvb-label" htmlFor="lvb-password">
                  Password
                </label>
                <span className="lvb-row-index">02</span>
              </div>
              <input
                id="lvb-password"
                className={
                  fieldErrors.password ? 'lvb-control is-invalid' : 'lvb-control'
                }
                type="password"
                name="password"
                autoComplete="current-password"
                placeholder="Password"
                value={password}
                onChange={handleChange('password')}
                aria-invalid={Boolean(fieldErrors.password)}
                aria-describedby={
                  fieldErrors.password ? 'lvb-password-error' : undefined
                }
                disabled={submitting}
              />
              {fieldErrors.password && (
                <p className="lvb-hint" id="lvb-password-error">
                  {fieldErrors.password}
                </p>
              )}
            </div>

            {/* ---- InlineVerificationError ---- */}
            <div
              className={verificationError ? 'lvb-error is-active' : 'lvb-error'}
              role="status"
              aria-live="polite"
            >
              <span className="lvb-error-mark" aria-hidden="true" />
              <p className="lvb-error-text">{verificationError}</p>
            </div>

            {/* ---- SubmitBar ---- */}
            <button
              type="submit"
              className="lvb-submit"
              disabled={submitting}
              aria-busy={submitting}
            >
              {submitting && <span className="lvb-spinner" aria-hidden="true" />}
              <span>{submitting ? 'Verifying' : 'Sign in'}</span>
            </button>
          </form>
        </div>

        {/* ================= RIGHT 5 — identity-context rail ================= */}
        <aside className="lvb-rail" aria-label="What verification unlocks">
          <div className="lvb-rail-head">
            <span className="lvb-micro">Verification Unlocks</span>
            <h2 className="lvb-rail-title">Protected destinations</h2>
            <p className="lvb-rail-note">
              Verified identities reach only the destinations their credentials own. Rail
              readouts are reference markings, not live data.
            </p>
          </div>

          {UNLOCKED_READOUTS.map((row) => (
            <div className="lvb-readout" key={row.label}>
              <span className="lvb-readout-label">{row.label}</span>
              <span className="lvb-readout-value">{row.value}</span>
            </div>
          ))}

          <a className="lvb-secondary" href="/sign-up">
            <span>Create an account</span>
            <span className="lvb-secondary-arrow" aria-hidden="true">
              →
            </span>
          </a>

          <div className="lvb-rail-foot">
            <p className="lvb-rail-foot-label">On record</p>
            {accountsLoading ? (
              <p className="lvb-rail-foot-label">Reading accounts…</p>
            ) : accountsError ? (
              <p className="lvb-rail-foot-label">Account records unavailable</p>
            ) : (
              <p className="lvb-rail-foot-value">
                {String(accounts.length).padStart(2, '0')}
              </p>
            )}
          </div>
        </aside>
      </div>
    </section>
  );
}

export default LoginVerificationBand;